// Robust image loader that handles HTTP/2 protocol errors and network issues

interface ImageLoadOptions {
  retries?: number;
  timeout?: number;
  fallbackToHttp1?: boolean;
  useCache?: boolean;
}

interface ImageLoadResult {
  success: boolean;
  url: string;
  error?: string;
  fromCache?: boolean;
}

// Cache for successfully loaded images
const imageLoadCache = new Map<string, boolean>();

// Convert HTTP/2 URL to HTTP/1.1 if needed
const getHttp1Url = (url: string): string => {
  // For your domain, we can try forcing HTTP/1.1 by adding a cache-busting parameter
  // This sometimes helps with HTTP/2 protocol issues
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=http1`;
};

// Load image with retry logic and HTTP/2 fallback
export const loadImageRobustly = async (
  src: string,
  options: ImageLoadOptions = {}
): Promise<ImageLoadResult> => {
  const {
    retries = 3,
    timeout = 10000,
    fallbackToHttp1 = true,
    useCache = true,
  } = options;

  // Check cache first
  if (useCache && imageLoadCache.has(src)) {
    return {
      success: true,
      url: src,
      fromCache: true,
    };
  }

  let lastError: string = '';

  // Try loading with different strategies
  const strategies = [
    { url: src, description: 'Original URL' },
    ...(fallbackToHttp1
      ? [{ url: getHttp1Url(src), description: 'HTTP/1.1 fallback' }]
      : []),
  ];

  for (const strategy of strategies) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await loadImageWithTimeout(strategy.url, timeout);

        // Cache successful loads
        if (useCache) {
          imageLoadCache.set(src, true);
        }

        return {
          success: true,
          url: strategy.url,
        };
      } catch (error: any) {
        lastError = error.message;
        console.warn(
          `❌ ${strategy.description} failed (attempt ${attempt}/${retries}):`,
          error.message
        );

        // Wait before retry (exponential backoff)
        if (attempt < retries) {
          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, attempt) * 500)
          );
        }
      }
    }
  }

  return {
    success: false,
    url: src,
    error: lastError,
  };
};

// Load image with timeout
const loadImageWithTimeout = (src: string, timeout: number): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let timeoutId: NodeJS.Timeout;

    const cleanup = () => {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout loading image: ${src}`));
    }, timeout);

    img.onload = () => {
      cleanup();
      resolve();
    };

    img.onerror = (error) => {
      cleanup();
      // Don't treat HTTP/2 protocol errors as failures if the image actually loads
      // The browser shows ERR_HTTP2_PROTOCOL_ERROR but the image loads successfully
      console.warn(`Image load warning for ${src}:`, error);
      reject(new Error(`Failed to load image: ${src} - ${error}`));
    };

    // Set loading attributes for better performance
    img.loading = 'eager';
    img.fetchPriority = 'high';
    img.crossOrigin = 'anonymous'; // This can help with some CORS/protocol issues

    // Add a small delay to handle HTTP/2 protocol timing issues
    setTimeout(() => {
      img.src = src;
    }, 10);
  });
};

// Preload multiple images with robust loading
export const preloadImagesRobustly = async (
  sources: string[],
  options: ImageLoadOptions = {}
): Promise<ImageLoadResult[]> => {
  const results = await Promise.allSettled(
    sources.map((src) => loadImageRobustly(src, options))
  );

  const loadResults: ImageLoadResult[] = results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      return {
        success: false,
        url: sources[index],
        error: result.reason?.message || 'Unknown error',
      };
    }
  });

  return loadResults;
};

// Clear the image cache
export const clearImageCache = (): void => {
  imageLoadCache.clear();
  console.log('🗑️ Image cache cleared');
};
